import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class DocumentValidatorService {

  constructor() { }

  /**
   * Validador personalizado para DNI, CUIL y CUIT argentinos
   * @param control Control del formulario
   * @returns ValidationErrors o null si es válido
   */
  static validateArgentineDocument(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    
    if (!value) {
      return null; // Si está vacío, deja que otros validadores manejen la obligatoriedad
    }

    const documentNumber = value.toString().replace(/\D/g, ''); // Eliminar caracteres no numéricos
    
    if (documentNumber.length < 7) {
      return { invalidDocument: { message: 'El documento debe tener al menos 7 dígitos' } };
    }

    // Validar DNI (7-8 dígitos)
    if (documentNumber.length >= 7 && documentNumber.length <= 8) {
      if (DocumentValidatorService.validateDNI(documentNumber)) {
        return null;
      } else {
        return { invalidDocument: { message: 'DNI inválido' } };
      }
    }

    // Validar CUIL/CUIT (11 dígitos)
    if (documentNumber.length === 11) {
      if (DocumentValidatorService.validateCUILCUIT(documentNumber)) {
        return null;
      } else {
        return { invalidDocument: { message: 'CUIL/CUIT inválido' } };
      }
    }

    // Si tiene entre 9 y 10 dígitos, o más de 11, es inválido
    if (documentNumber.length === 9 || documentNumber.length === 10) {
      return { invalidDocument: { message: 'Formato de documento inválido. Use 7-8 dígitos para DNI o 11 para CUIL/CUIT' } };
    }

    if (documentNumber.length > 11) {
      return { invalidDocument: { message: 'El documento no puede tener más de 11 dígitos' } };
    }

    return { invalidDocument: { message: 'Formato de documento inválido' } };
  }

  /**
   * Valida un DNI argentino
   * @param dni Número de DNI como string
   * @returns true si es válido, false si no
   */
  private static validateDNI(dni: string): boolean {
    // DNI válido: entre 1.000.000 y 99.999.999
    const dniNumber = parseInt(dni, 10);
    return dniNumber >= 1000000 && dniNumber <= 99999999;
  }

  /**
   * Valida un CUIL/CUIT argentino usando el algoritmo de dígito verificador
   * @param cuilCuit Número de CUIL/CUIT como string de 11 dígitos
   * @returns true si es válido, false si no
   */
  private static validateCUILCUIT(cuilCuit: string): boolean {
    if (cuilCuit.length !== 11) {
      return false;
    }

    // Verificar que todos sean dígitos
    if (!/^\d{11}$/.test(cuilCuit)) {
      return false;
    }

    // Verificar prefijos válidos según AFIP (20, 23, 24, 27, 30, 33, 34)
    const prefix = parseInt(cuilCuit.substring(0, 2), 10);
    const validPrefixes = [20, 23, 24, 27, 30, 33, 34];
    if (!validPrefixes.includes(prefix)) {
      return false;
    }

    // Extraer los primeros 10 dígitos y el dígito verificador
    const digits = cuilCuit.split('').map(d => parseInt(d, 10));
    const checkDigit = digits[10];
    const baseDigits = digits.slice(0, 10);

    // Multiplicadores para el cálculo del dígito verificador
    const multipliers = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];

    // Calcular la suma ponderada
    let sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += baseDigits[i] * multipliers[i];
    }

    // Calcular el resto de la división por 11
    const remainder = sum % 11;
    let expectedCheckDigit = 11 - remainder;

    // Casos especiales según algoritmo AFIP
    if (expectedCheckDigit === 11) {
      expectedCheckDigit = 0;
    } else if (expectedCheckDigit === 10) {
      // Caso especial: cuando el resultado es 10, el CUIT/CUIL no es válido
      // según el algoritmo oficial, pero algunos sistemas lo convierten a 9
      expectedCheckDigit = 9;
    }

    return checkDigit === expectedCheckDigit;
  }

  /**
   * Formatea un número de documento argentino
   * @param documentNumber Número sin formato
   * @param documentType Tipo de documento (opcional)
   * @returns Documento formateado
   */
  static formatDocument(documentNumber: string, documentType?: string): string {
    if (!documentNumber) return '';
    
    const cleanNumber = documentNumber.replace(/\D/g, '');
    
    // Formatear CUIL/CUIT: XX-XXXXXXXX-X
    if (cleanNumber.length === 11) {
      return `${cleanNumber.substring(0, 2)}-${cleanNumber.substring(2, 10)}-${cleanNumber.substring(10)}`;
    }
    
    // Formatear DNI: XX.XXX.XXX
    if (cleanNumber.length >= 7 && cleanNumber.length <= 8) {
      if (cleanNumber.length === 7) {
        return `${cleanNumber.substring(0, 1)}.${cleanNumber.substring(1, 4)}.${cleanNumber.substring(4)}`;
      } else {
        return `${cleanNumber.substring(0, 2)}.${cleanNumber.substring(2, 5)}.${cleanNumber.substring(5)}`;
      }
    }
    
    return cleanNumber;
  }

  /**
   * Obtiene el tipo de documento basado en la longitud y formato
   * @param documentNumber Número de documento
   * @returns Tipo de documento detectado
   */
  static getDocumentType(documentNumber: string): string {
    if (!documentNumber) return '';
    
    const cleanNumber = documentNumber.replace(/\D/g, '');
    
    if (cleanNumber.length >= 7 && cleanNumber.length <= 8) {
      return 'DNI';
    } else if (cleanNumber.length === 11) {
      // Verificar prefijo para determinar si es CUIL o CUIT
      const prefix = cleanNumber.substring(0, 2);
      
      // Prefijos exclusivos de personas jurídicas (CUIT)
      if (['30', '33', '34'].includes(prefix)) {
        return 'CUIT';
      } 
      // Prefijos que pueden ser tanto CUIL como CUIT (personas físicas)
      else if (['20', '23', '24', '27'].includes(prefix)) {
        return 'CUIL/CUIT'; // Ambos son válidos
      }
      // Otros prefijos menos comunes
      else {
        return 'CUIL/CUIT';
      }
    }
    
    return 'Desconocido';
  }

  /**
   * Detecta si un documento con prefijo ambiguo es más probable que sea CUIT
   * basado en patrones heurísticos (esto es una aproximación)
   * @param documentNumber Número de documento
   * @returns true si es probable que sea CUIT
   */
  static isLikelyCUIT(documentNumber: string): boolean {
    if (!documentNumber) return false;
    
    const cleanNumber = documentNumber.replace(/\D/g, '');
    
    if (cleanNumber.length !== 11) return false;
    
    const prefix = cleanNumber.substring(0, 2);
    
    // Solo aplicar heurística a prefijos ambiguos
    if (!['20', '23', '24', '27'].includes(prefix)) {
      return ['30', '33', '34'].includes(prefix);
    }
    
    // Heurística simple: números muy altos en los dígitos centrales
    // pueden indicar CUIT de empresa (esto es solo una aproximación)
    const middleSection = cleanNumber.substring(2, 10);
    const middleNumber = parseInt(middleSection, 10);
    
    // Si el número es muy alto, es más probable que sea CUIT empresarial
    // (esto es solo una heurística, no una regla estricta)
    return middleNumber > 30000000;
  }

  /**
   * Obtiene el tipo de documento con mayor precisión usando heurísticas
   * @param documentNumber Número de documento
   * @returns Tipo de documento detectado con heurísticas
   */
  static getDocumentTypeWithHeuristics(documentNumber: string): string {
    const baseType = this.getDocumentType(documentNumber);
    
    if (baseType === 'CUIL/CUIT') {
      // Aplicar heurística para determinar si es más probable que sea CUIT
      if (this.isLikelyCUIT(documentNumber)) {
        return 'CUIT';
      } else {
        return 'CUIL';
      }
    }
    
    return baseType;
  }
}
